import React from 'react';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import { Navigate } from 'react-router-dom';
import Route from 'enum/route.enum';
import MenuId from 'enum/menu-id.enum';
import { translate as t } from 'utils/i18n';
import { trackPageItem } from 'components/main/common/utils';

import {
  MenuModal,
  MenuModalItemImage,
  ChannelsFooter,
  ChannelMenuItem,
  Header,
  Container,
  ChannelPlayerStage,
  ChannelLoadingBackdrop,
  ChannelLoadingPanel,
  ChannelLoadingTitle,
  ChannelLoadingMessage,
} from 'components/styles/Common.styled';
import BackCursor from 'components/common/nav/backCursor';
import BaseChannelTemplate from './BaseChannelTemplate';
import { measureComponent } from 'utils/common/measureComponent';
import { deepClone } from 'utils/common/deepClone';
import CachedImg from 'components/common/CachedImg/CachedImg';
import { updatePlayerState } from './common/channelPlayerState';
import {
  RadioIcon,
  RadioPlaybackLayer,
  RadioNowPlayingStage,
  RadioStationCard,
  RadioStationIconBox,
  RadioLiveBadge,
  RadioStationTitle,
  RadioStationFrequency,
  RadioSignalBars,
  BaselineHiddenPlayerMount,
} from './baselineRadio.styles';

/** Auto-hide bottom channel strip when remote is idle (fullscreen BaseLine). */
var BASELINE_LIST_HIDE_MS = 8000;

/**
 * Baseline Live TV and Radio — horizontal bottom carousel.
 * Pass isRadio={true} for the radio screen (same component, radio mode).
 *
 * BaseLine TV is fullscreen-only (no preview window). Channel strip auto-hides on idle.
 * Browse (arrows / click other) moves highlight only; Enter / click-selected zaps.
 */
class ChannelsList extends BaseChannelTemplate {
  static defaultProps = {
    isRadio: false,
  };

  constructor(props) {
    super(props);
    this.state = Object.assign({}, this.state, {
      playingIndex: 0,
    });
  }

  isRadioMode() {
    return Boolean(this.props.isRadio);
  }

  trackChannelItem() {
    const { items, focusIndex } = this.state;
    if (!items || !items[focusIndex]) {
      return;
    }
    const menuId = this.isRadioMode() ? MenuId.RADIO : MenuId.CHANNELS;
    trackPageItem(menuId, items[focusIndex]?.channel_id?.locale_english?.locale_name);
  }

  renderErrorStates() {
    if (!this.isRadioMode()) {
      return super.renderErrorStates();
    }

    const { loading, showError, items } = this.state;
    return (
      <>
        {items.length <= 0 && !showError && !loading && (
          <div className={`has-text-danger has-text-centered`}>
            <p className={'is-size-4 has-text-bold'}>{t('Radio.radio_error')}</p>
          </div>
        )}
        {items.length <= 0 && showError && !loading && (
          <div className={`has-text-danger has-text-centered`}>
            <p className={'is-size-4 has-text-bold'}>
              The selected language is not configured yet.
            </p>
          </div>
        )}
      </>
    );
  }

  componentDidMount() {
    updatePlayerState({ displayFullScreen: true });
    this._clearTepPreviewClasses();
    super.componentDidMount();
  }

  componentWillUnmount() {
    this._clearTepPreviewClasses();
    super.componentWillUnmount();
  }

  _clearTepPreviewClasses() {
    try {
      document.documentElement.classList.remove('tep-preview-active', 'tep-preview-pending');
      document.body.classList.remove('tep-preview-active', 'tep-preview-pending');
    } catch (e) {}
  }

  getMeasurements(sampleItem) {
    return measureComponent(() => this.renderChannelItem(sampleItem, false));
  }

  renderChannelItem(item, isLiveItem, key = null, refCallback = null, onClick = null) {
    const locale_name = item.channel_id?.locale_channel?.locale_name;
    const image = item.channel_id?.channel?.image_url;
    const isActive = !!item.active;
    const isRadio = this.isRadioMode();

    return (
      <ChannelMenuItem
        active={isActive}
        ref={refCallback}
        className={isActive ? 'menu-item is-active' : 'menu-item'}
        key={key}
        id='menu-item'
        onClick={onClick}
      >
        <MenuModalItemImage>
          {isRadio ? (
            <RadioIcon variant='outline' width={48} height={48} />
          ) : (
            <CachedImg src={image} width='80px' height='80px' alt='' />
          )}
        </MenuModalItemImage>
        <span>{locale_name}</span>
      </ChannelMenuItem>
    );
  }

  slideShowList() {
    if (!this._isMounted) return;

    clearTimeout(this.state.timeOut);

    if (this.isRadioMode()) {
      this.setState({
        slideList: true,
        timeOut: null,
      });
      return;
    }

    this.setState({
      slideList: true,
      timeOut: setTimeout(() => {
        if (this._isMounted) {
          this.setState({ slideList: false });
        }
      }, BASELINE_LIST_HIDE_MS),
    });
  }

  async screenResolution() {
    this.slideShowList();
  }

  async checkResolution(redirectBack) {
    if (!redirectBack) return;
    await this.stopPlayer();
    this.setState({ redirectBack });
  }

  resolveCatalogIndex(item) {
    var activeItems = this.getActiveItems();
    if (!activeItems || !item || !item.channel_id || !item.channel_id.channel) {
      return -1;
    }
    var channelId = item.channel_id.channel.id;
    return activeItems.findIndex(function (entry) {
      return entry && entry.channel_id && entry.channel_id.channel && entry.channel_id.channel.id === channelId;
    });
  }

  async browseChannelIndex(newIndex) {
    var activeItems = this.getActiveItems();
    if (!activeItems || activeItems.length === 0) return;

    newIndex = this.channelNav.wrapChannelIndex(newIndex, activeItems.length);
    if (newIndex === this.state.focusIndex) return;

    var channelsList = await this.generateNewChannelList(newIndex, activeItems);
    if (newIndex >= 0 && newIndex < activeItems.length) {
      this.setState({
        focusIndex: newIndex,
        channelsList: channelsList,
      });
      clearTimeout(this.state.timeOut);
      this.slideShowList();
    }
  }

  async tuneChannelIndex(newIndex) {
    var activeItems = this.getActiveItems();
    if (!activeItems || activeItems.length === 0) return;

    newIndex = this.channelNav.wrapChannelIndex(newIndex, activeItems.length);

    var channelsList = await this.generateNewChannelList(newIndex, activeItems);
    if (newIndex >= 0 && newIndex < activeItems.length) {
      this.setState({
        focusIndex: newIndex,
        playingIndex: newIndex,
        channelsList: channelsList,
      });
      clearTimeout(this.state.timeOut);
      this.slideShowList();
      this.checkDevice(newIndex);
    }
  }

  async changeChannelIndex(newIndex) {
    return this.tuneChannelIndex(newIndex);
  }

  onChannelItemClick(item) {
    var catalogIndex = this.resolveCatalogIndex(item);
    if (catalogIndex < 0) return;

    if (catalogIndex === this.state.focusIndex) {
      this.tuneChannelIndex(catalogIndex);
    } else {
      this.browseChannelIndex(catalogIndex);
    }
  }

  getKeyPressCallbacks() {
    if (this.isRadioMode()) {
      const base = super.getKeyPressCallbacks();
      return {
        ...base,
        onVerticalMove: (delta) => this.previewChannelIndex(this.state.focusIndex + delta),
        onHorizontalMove: (delta) => this.previewChannelIndex(this.state.focusIndex + delta),
        onEnter: () => this.confirmRadioTune(),
      };
    }

    var self = this;
    var base = super.getKeyPressCallbacks();
    return {
      onVerticalMove: function (delta) {
        self.browseChannelIndex(self.state.focusIndex + delta);
      },
      onHorizontalMove: function (delta) {
        self.browseChannelIndex(self.state.focusIndex + delta);
      },
      onEnter: function () {
        self.slideShowList();
        if (self.state.focusIndex !== self.state.playingIndex) {
          self.tuneChannelIndex(self.state.focusIndex);
        }
      },
      onBack: base.onBack,
      onChannelNumber: async function (keyCode) {
        self.slideShowList();
        return base.onChannelNumber(keyCode);
      },
    };
  }

  async previewChannelIndex(newIndex) {
    const activeItems = this.getActiveItems();
    if (!activeItems || activeItems.length === 0) return;

    newIndex = this.channelNav.wrapChannelIndex(newIndex, activeItems.length);
    if (newIndex === this.state.focusIndex) return;

    const channelsList = await this.generateNewChannelList(newIndex, activeItems);
    if (newIndex >= 0 && newIndex < activeItems.length) {
      this.setState({
        focusIndex: newIndex,
        channelsList,
      });
      clearTimeout(this.state.timeOut);
      this.slideShowList();
    }
  }

  async tuneToChannel(newIndex) {
    const activeItems = this.getActiveItems();
    if (!activeItems || activeItems.length === 0) return;

    newIndex = this.channelNav.wrapChannelIndex(newIndex, activeItems.length);
    if (newIndex === this.state.focusIndex && newIndex === this.state.playingIndex) return;

    const channelsList = await this.generateNewChannelList(newIndex, activeItems);
    if (newIndex >= 0 && newIndex < activeItems.length) {
      this.setState({
        focusIndex: newIndex,
        channelsList,
      });
      await this.checkDevice(newIndex);
      clearTimeout(this.state.timeOut);
      this.slideShowList();
    }
  }

  setChannelIndex(channelIndx) {
    const index = this.channelNav.getChannelIndexFromNumbers(channelIndx);
    if (this.isRadioMode()) {
      this.tuneToChannel(index);
      return;
    }
    this.changeChannelIndex(index);
  }

  async confirmRadioTune() {
    const { focusIndex, playingIndex } = this.state;
    if (focusIndex !== playingIndex) {
      await this.checkDevice(focusIndex);
      return;
    }
    await this.screenResolution();
  }

  async checkDevice(focusIndex) {
    await super.checkDevice(focusIndex);
    if (this._isMounted && typeof focusIndex === 'number') {
      this.setState({ playingIndex: focusIndex });
    }
  }

  renderChannelList(channelsList, slideList) {
    return (
      <ChannelsFooter hidden={!slideList}>
        <MenuModal
          ref={(ref) => (this.scrollRef = ref)}
          id='menu-bar'
          className={`menu-bar w-10 ${!slideList ? 'is-hidden' : ''}`}
        >
          {channelsList.map((item, i) => {
            const channel_id = item['channel_id'];
            if (
              !channel_id ||
              !channel_id['locale_channel'] ||
              !channel_id['locale_channel'].locale_name
            ) {
              return null;
            }

            if (this.isRadioMode()) {
              const onItemClick = () => {
                const globalIndex = this.getActiveItems().findIndex((ch) => ch.id === item.id);
                if (globalIndex >= 0) {
                  this.tuneToChannel(globalIndex);
                }
              };
              return this.renderChannelItem(item, true, i, null, onItemClick);
            }

            var itemKey = (channel_id.channel && channel_id.channel.id) || item.id || i;
            return this.renderChannelItem(item, true, itemKey, null, () =>
              this.onChannelItemClick(item)
            );
          })}
        </MenuModal>
      </ChannelsFooter>
    );
  }

  renderChannelLoadingOverlay() {
    const { channelLoading, channelSignalUnavailable, items, playingIndex } = this.state;
    if (!channelLoading && !channelSignalUnavailable) return null;

    const channelName = items?.[playingIndex]?.channel_id?.locale_channel?.locale_name ?? '';

    return (
      <ChannelLoadingBackdrop>
        <ChannelLoadingPanel>
          {channelSignalUnavailable ? (
            <>
              <ChannelLoadingMessage>{t('Channels.signal_unavailable')}</ChannelLoadingMessage>
              {channelName ? <ChannelLoadingTitle>{channelName}</ChannelLoadingTitle> : null}
            </>
          ) : (
            <>
              <div className='loader is-loading' style={{ height: 72, width: 72 }} />
              {channelName ? <ChannelLoadingTitle>{channelName}</ChannelLoadingTitle> : null}
            </>
          )}
        </ChannelLoadingPanel>
      </ChannelLoadingBackdrop>
    );
  }

  getCarouselSlotCount(totalChannels, numberOfChannels) {
    if (this.isRadioMode() && totalChannels <= numberOfChannels) {
      return totalChannels;
    }

    return numberOfChannels < totalChannels
      ? numberOfChannels
      : totalChannels % 2 !== 0
        ? totalChannels
        : totalChannels - 1;
  }

  async generateNewChannelList(newIndex, list) {
    const clonedList = deepClone(list);
    const { allowedChannels: numberOfChannels } = this.state;
    const channelsList = [];

    const existingChannelsMap = new Map(
      this.state.channelsList.map((item) => [item.channel_id.channel.id, item])
    );

    const totalChannels = clonedList.length;
    const maxIterations = this.getCarouselSlotCount(totalChannels, numberOfChannels);

    for (let i = 0; i < maxIterations; i++) {
      const index = (newIndex - Math.floor(maxIterations / 2) + i + totalChannels) % totalChannels;
      const item = clonedList[index];
      if (item) {
        const existingItem = existingChannelsMap.get(item.channel_id.channel.id);
        if (existingItem) {
          existingItem['active'] = index === newIndex;
          channelsList.push(existingItem);
        } else {
          item['active'] = index === newIndex;
          channelsList.push(item);
        }
      } else {
        const wrappedIndex = i % totalChannels;
        const wrappedItem = clonedList[wrappedIndex];
        if (wrappedItem) {
          wrappedItem['active'] = wrappedIndex === newIndex;
          channelsList.push(wrappedItem);
        }
      }
    }

    return channelsList;
  }

  getRadioSignalBars(count = 12) {
    const bars = [];
    for (let i = 0; i < count; i++) {
      bars.push(<span key={i} style={{ animationDelay: `${(i % 5) * 0.12}s` }} />);
    }
    return bars;
  }

  renderPlaybackStage() {
    if (!this.isRadioMode()) {
      return null;
    }

    const { items, loading, playingIndex, channelLoading, channelSignalUnavailable } = this.state;
    if (loading || !items.length) {
      return null;
    }

    const showNowPlaying = !channelLoading && !channelSignalUnavailable;
    if (!showNowPlaying) {
      return null;
    }

    const activePlayingIndex =
      typeof playingIndex === 'number' && playingIndex < items.length ? playingIndex : 0;
    const activeChannel = items[activePlayingIndex];
    const title = activeChannel?.channel_id?.locale_channel?.locale_name ?? '';
    const frequency = activeChannel?.channel_id?.locale_channel?.locale_description ?? '';

    return (
      <RadioPlaybackLayer>
        <RadioNowPlayingStage>
          <RadioStationCard>
            <RadioStationIconBox>
              <RadioIcon variant='outline' width={90} height={90} />
            </RadioStationIconBox>
            <RadioLiveBadge $active={Boolean(activeChannel)}>{t('Radio.live')}</RadioLiveBadge>
            <RadioStationTitle>{title}</RadioStationTitle>
            <RadioStationFrequency>{frequency || t('Radio.playing_now')}</RadioStationFrequency>
            {activeChannel && <RadioSignalBars>{this.getRadioSignalBars()}</RadioSignalBars>}
          </RadioStationCard>
        </RadioNowPlayingStage>
      </RadioPlaybackLayer>
    );
  }

  getPlayerContainers() {
    if (!this.isRadioMode()) {
      return super.getPlayerContainers();
    }
    return <BaselineHiddenPlayerMount>{super.getPlayerContainers()}</BaselineHiddenPlayerMount>;
  }

  render() {
    if (this.state.redirectBack) {
      return <Navigate to={Route.route.LANDING} />;
    }

    let { loading, showError, items, channelsList, slideList, showList, channelOnly } = this.state;

    try {
      console.log('CHANNELS: render state', {
        loading,
        showError,
        itemsLength: items ? items.length : 0,
        channelsListLength: channelsList ? channelsList.length : 0,
        slideList,
        showList,
        channelOnly,
        isRadio: this.isRadioMode(),
        isWEBOS: Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST).isWEBOS,
      });
    } catch (e) {}

    if (Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST).isWEBOS) {
      showList = this.channelVis.shouldForceShowList(this.state);
    }

    const shouldShowChannelList = channelsList.length > 0 && !showError && !loading;

    return (
      <div>
        <>
          {<Header></Header>}
          {!channelOnly && <BackCursor />}
          {this.renderPlaybackStage()}
          {<Container>{this.renderErrorStates()}</Container>}
          <ChannelPlayerStage>
            {this.getPlayerContainers()}
            {this.renderChannelLoadingOverlay()}
          </ChannelPlayerStage>
          {shouldShowChannelList && slideList && this.renderChannelList(channelsList, slideList)}
        </>
      </div>
    );
  }
}

export default ChannelsList;
